import React, { useState, useRef, useEffect } from "react";
import { AccountDeliveryDriverType } from "../types/AccountDeliveryDriverType";
import { TableColumnsType, TablePaginationConfig } from "antd";
import { Button, Dropdown, Tooltip } from "antd";
import { DownOutlined, EyeOutlined } from "@ant-design/icons";
import { Table } from "antd";
import { Tag } from "antd";
import { message } from "antd";
import { updateAccountDeliveryDriverDutyStatus } from "../graphql/mutations/updateAccountDeliveryDriverDutyStatus";
import { accountStatusUpdateMutations } from "../../../../graphql/mutations/account/accountStatusUpdateMutations";
import { viewDriverPhone } from "../graphql/mutations/viewDriverPhone";
import { maskPhoneForExport } from "../../../../utils/exportDesensitize";
import { t } from '../../../../i18n';

export interface AccountsColumnsProps {
    accountDeliveryDrivers: AccountDeliveryDriverType[];
    showAccountDeliveryDriverDetail: (record: AccountDeliveryDriverType) => void;
    vehicleTypeOptions: Array<{ label: string; value: number }>;
    onDataUpdated?: () => void; // 数据更新后的回调
    pagination?: TablePaginationConfig;
    onChange?: (pagination: any) => void;
    onShowOperationLog?: (accountId: number) => void; // 显示操作记录
    onResignDriver?: (record: AccountDeliveryDriverType) => void; // 司机离职
}

export const AccountDeliveryDriverTable: React.FC<AccountsColumnsProps> = ({
    accountDeliveryDrivers,
    showAccountDeliveryDriverDetail,
    vehicleTypeOptions,
    onDataUpdated,
    pagination,
    onChange,
    onShowOperationLog,
    onResignDriver
}) => {
    const [revealedPhones, setRevealedPhones] = useState<Record<number, string>>({});
    const timerRefs = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

    useEffect(() => {
        const timers = timerRefs.current;
        return () => {
            Object.values(timers).forEach(clearTimeout);
        };
    }, []);

    const handleViewPhone = async (accountId: number) => {
        try {
            const phone = await viewDriverPhone(accountId);
            setRevealedPhones(prev => ({ ...prev, [accountId]: phone }));
            if (timerRefs.current[accountId]) {
                clearTimeout(timerRefs.current[accountId]);
            }
            timerRefs.current[accountId] = setTimeout(() => {
                setRevealedPhones(prev => {
                    const next = { ...prev };
                    delete next[accountId];
                    return next;
                });
            }, 10000);
        } catch {
            message.error(t('delivery.personnel.message.viewPhoneFail'));
        }
    };

    const handleStatusChange = async (record: AccountDeliveryDriverType, status: string) => {
        const isOnDuty = status === 'On duty';
        try {
            // Defensive: some environments may return null/undefined id; avoid runtime crash (undefined.toString()).
            if (record?.id === null || record?.id === undefined) {
                message.error(t('delivery.personnel.message.dutyStatusUpdateFail'));
                return;
            }
            await updateAccountDeliveryDriverDutyStatus(record.id, isOnDuty);
            message.success(t('delivery.personnel.message.dutyStatusUpdateSuccess'));
            // 更新成功后，调用父组件的刷新方法
            // 这里可以通过回调或者事件来通知父组件刷新数据
            onDataUpdated?.();
        } catch (error) {
            message.error(t('delivery.personnel.message.dutyStatusUpdateFail'));
            console.error('Update duty status error:', error);
        }
    };

    const handleAccountStatusChange = async (record: AccountDeliveryDriverType, status: string) => {
        const accountStatus = status === 'Active' ? 1 : 0;
        try {
            await accountStatusUpdateMutations(record.account.id!, accountStatus);
            message.success(t('delivery.personnel.message.personnelStatusUpdateSuccess'));
            // 更新成功后，调用父组件的刷新方法
            // 这里可以通过回调或者事件来通知父组件刷新数据
            onDataUpdated?.();
        } catch (error) {
            message.error(t('delivery.personnel.message.personnelStatusUpdateFail'));
            console.error('Update account status error:', error);
        }
    };

    // 获取审核状态显示文本
    const getVerificationStatusText = (status: number | undefined): string => {
        if (status === undefined || status === null) {
            return '-';
        }
        switch (status) {
            case 0:
                return t('delivery.driver.verificationStatusNotReady');
            case 1:
                return t('delivery.driver.verificationStatusPending');
            case 2:
                return t('delivery.driver.verificationStatusApproved');
            case 3:
                return t('delivery.driver.verificationStatusRejected');
            default:
                return '-';
        }
    };

    // 获取证件类型显示文本
    const getIdDocumentTypeText = (type: number | null | undefined): string => {
        if (type === null || type === undefined) {
            return '-';
        }
        switch (type) {
            case 0:
                return t('delivery.driver.driverLicense');
            // case 1:
                // return t('delivery.driver.idCard');
            // case 2:
                // return t('delivery.driver.passport');
            // case 3:
                // return t('delivery.driver.ead');
            default:
                return '-';
        }
    };

    const columns: TableColumnsType<AccountDeliveryDriverType> = [
        {
            title: t('delivery.personnel.label.id'),
            dataIndex: 'id',
            key: 'id',
            align: 'left' as const,
            render: (text, record) => <Button
                onClick={() => showAccountDeliveryDriverDetail(record)}
                type="link"
                style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0, paddingBottom: 0, marginLeft: 0, marginRight: 0 }}
                className="!p-0 !m-0"
            >
                {text}
            </Button>,
        },
        {
            title: t('delivery.personnel.label.name'),
            dataIndex: ['account', 'name'],
            key: 'account.name',
            align: 'left' as const,
            render: (text: string, record: AccountDeliveryDriverType) => (
                <span>
                    {text}
                    {record.isBlacklist && (
                        <Tag color="error" className="ml-2">{t('delivery.driver.accountStatusBanned')}</Tag>
                    )}
                </span>
            ),
        },
        {
            title: t('delivery.personnel.label.phone'),
            dataIndex: ['account', 'phone'],
            key: 'account.phone',
            align: 'left' as const,
            render: (_text, record) => {
                const accountId = record.account.id;
                const revealed = accountId != null ? revealedPhones[accountId] : undefined;
                const masked = `+${record.account.countryCode} ${maskPhoneForExport(record.account.phone?.toString())}`;
                return (
                    <span className="flex items-center gap-1">
                        <span>{revealed ?? masked}</span>
                        {accountId != null && (
                            <Button
                                type="link"
                                icon={<EyeOutlined />}
                                size="small"
                                className="!p-0"
                                onClick={() => handleViewPhone(accountId)}
                            >
                                {t('delivery.personnel.label.view')}
                            </Button>
                        )}
                    </span>
                );
            },
        },
        {
            title: t('delivery.personnel.label.vehicleType'),
            dataIndex: 'vehicleTypeId',
            key: 'vehicleTypeId',
            align: 'left' as const,
            render: (text) => vehicleTypeOptions.find(v => v.value === text)?.label,
        },
        {
            title: t('delivery.personnel.label.vehiclePlate'),
            dataIndex: 'vehiclePlate',
            key: 'vehiclePlate',
            align: 'left' as const,
        },
        {
            title: t('delivery.personnel.label.isEmployed'),
            dataIndex: 'isEmployed',
            key: 'isEmployed',
            align: 'left' as const,
            render: (isEmployed: boolean | undefined) => {
                if (isEmployed === undefined || isEmployed === null) {
                    return '-';
                }
                return isEmployed ? t('delivery.personnel.label.employed') : t('delivery.personnel.label.unemployed');
            },
        },
        // {
        //     title: t('delivery.personnel.label.dutyStatus'),
        //     dataIndex: 'isOnDuty',
        //     key: 'isOnDuty',
        //     align: 'left' as const,
        //     filters: [
        //         {
        //             text: t('delivery.personnel.label.onDuty'),
        //             value: true,
        //         },
        //         {
        //             text: t('delivery.personnel.label.offDuty'),
        //             value: false,
        //         },
        //     ],
        //     onFilter: (value, record) => record.isOnDuty === value,
        //     render: (isOnDuty: boolean, record: AccountDeliveryDriverType) => {
        //         const isResigned = record.isEmployed === false;
        //         const items = [
        //             {
        //                 key: 'On duty',
        //                 label: t('delivery.personnel.label.onDuty'),
        //                 onClick: () => handleStatusChange(record, 'On duty'),
        //                 disabled: isResigned,
        //             },
        //             {
        //                 key: 'Off duty',
        //                 label: t('delivery.personnel.label.offDuty'),
        //                 onClick: () => handleStatusChange(record, 'Off duty'),
        //                 disabled: isResigned,
        //             },
        //         ];
        //
        //         return (
        //             <Dropdown menu={{ items }} trigger={['click']} disabled={isResigned}>
        //                 <Button type="link" disabled={isResigned}>
        //                     {isOnDuty ? t('delivery.personnel.label.onDuty') : t('delivery.personnel.label.offDuty')} <DownOutlined className="ml-1 text-xs" />
        //                 </Button>
        //             </Dropdown>
        //         );
        //     },
        // },
        // {
        //     title: t('delivery.personnel.label.personnelStatus'),
        //     dataIndex: ['account', 'status'],
        //     key: 'account.status',
        //     align: 'left' as const,
        //     filters: [
        //         {
        //             text: t('delivery.personnel.label.active'),
        //             value: 1,
        //         },
        //         {
        //             text: t('delivery.personnel.label.inactive'),
        //             value: 0,
        //         },
        //     ],
        //     onFilter: (value, record) => record.account.status === value,
        //     render: (status: number, record: AccountDeliveryDriverType) => {
        //         const isResigned = record.isEmployed === false;
        //         const items = [
        //             {
        //                 key: 'Active',
        //                 label: t('delivery.personnel.label.active'),
        //                 onClick: () => handleAccountStatusChange(record, 'Active'),
        //                 disabled: isResigned,
        //             },
        //             {
        //                 key: 'Inactive',
        //                 label: t('delivery.personnel.label.inactive'),
        //                 onClick: () => handleAccountStatusChange(record, 'Inactive'),
        //                 disabled: isResigned,
        //             },
        //         ];
        //
        //         return (
        //             <Dropdown menu={{ items }} trigger={['click']} disabled={isResigned}>
        //                 <Button type="link" disabled={isResigned}>
        //                     {Number(status) === 1 ? t('delivery.personnel.label.active') : t('delivery.personnel.label.inactive')} <DownOutlined className="ml-1 text-xs" />
        //                 </Button>
        //             </Dropdown>
        //         );
        //     },
        // },
        {
            title: t('delivery.personnel.label.phoneStatus'),
            dataIndex: ['account', 'phoneStatus'],
            key: 'account.phoneStatus',
            render: (phoneStatus: number | undefined) => {
                if (phoneStatus === undefined || phoneStatus === null) {
                    return t('delivery.personnel.label.unverified');
                }
                return phoneStatus === 1 
                    ? t('delivery.personnel.label.verified') 
                    : t('delivery.personnel.label.unverified');
            },
        },
        {
            title: t('delivery.driver.verificationStatus'),
            dataIndex: 'verificationStatus',
            key: 'verificationStatus',
            align: 'left' as const,
            render: (status: number | undefined) => {
                const statusText = getVerificationStatusText(status);
                // 对于信息不全的状态（0），显示特殊提示
                if (status === 0) {
                    return (
                        <span style={{ color: '#faad14' }}>
                            {statusText}
                        </span>
                    );
                }
                return statusText;
            },
        },
        {
            title: t('delivery.driver.failureReason'),
            dataIndex: 'reviewComment',
            key: 'reviewComment',
            width: 200,
            render: (reviewComment: string | null | undefined, record: AccountDeliveryDriverType) => {
                if (record.verificationStatus === 3 && reviewComment) {
                    const truncatedComment = reviewComment.length > 20 
                        ? `${reviewComment.substring(0, 20)}...` 
                        : reviewComment;
                    return (
                        <Tooltip title={reviewComment}>
                            <span>
                                {truncatedComment}
                            </span>
                        </Tooltip>
                    );
                }
                return '-';
            },
        },
        {
            title: t('delivery.driver.idDocumentType'),
            dataIndex: 'idDocumentType',
            key: 'idDocumentType',
            align: 'left' as const,
            render: (type: number | null | undefined) => getIdDocumentTypeText(type),
        },
        {
            title: t('delivery.driver.idDocumentNumber'),
            dataIndex: 'idDocumentNumber',
            key: 'idDocumentNumber',
            align: 'left' as const,
            render: (text: string | null | undefined) => text || '-',
        },
        {
            title: t('delivery.personnel.label.operationLog'),
            key: 'operationLog',
            align: 'left' as const,
            width: 160,
            render: (_: any, record: AccountDeliveryDriverType) => (
                <div className="flex items-center gap-2 whitespace-nowrap">
                    <Button
                        type="link"
                        onClick={() => onResignDriver?.(record)}
                        disabled={record.isEmployed === false || !onResignDriver}
                    >
                        {t('delivery.personnel.label.buttonUnemployed')}
                    </Button>
                    <Button
                        type="link"
                        onClick={() => {
                            if (record.account?.id) {
                                onShowOperationLog?.(record.account.id);
                            }
                        }}
                    >
                        {t('delivery.personnel.label.operationLogView')}
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <div className="[&_.ant-table-tbody>tr>td:first-child]:!pl-4 [&_.ant-table-thead>tr>th:first-child]:!pl-4">
        <Table
            rowKey="id"
            columns={columns}
            dataSource={accountDeliveryDrivers}
            className="mb-0 mt-4 mb-4"
            pagination={false}
            onChange={onChange}
            scroll={{x: 'max-content'}}
            size="middle"
            rowClassName={(record) => record.isBlacklist ? 'bg-gray-100' : ''}
        />
        </div>
    );
};